/**
 * Utility function để nén ảnh trước khi gửi lên server
 * Giới hạn: max 1MB/ảnh để tuân thủ Vercel 4.5MB request limit
 */

/**
 * Nén ảnh từ File object thành base64 data URL
 * @param {File} file - Image file từ input
 * @param {number} maxSizeMB - Kích thước tối đa sau nén (default 1MB)
 * @returns {Promise<Object>} - { dataUrl, originalSize, compressedSize }
 */
export const compressImage = async (file, maxSizeMB = 1) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Không có file ảnh"));
      return;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      reject(new Error("Định dạng ảnh không được hỗ trợ. Chỉ hỗ trợ: JPG, PNG, GIF, WebP"));
      return;
    }

    const originalSize = file.size;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // Nếu file đã nhỏ hơn giới hạn, không cần nén
    if (originalSize < maxSizeBytes) {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve({
          dataUrl: event.target.result,
          originalSize,
          compressedSize: originalSize,
          compressed: false,
        });
      };
      reader.onerror = () => reject(new Error("Lỗi đọc file"));
      reader.readAsDataURL(file);
      return;
    }

    // Cần nén ảnh
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Tính toán kích thước mới
          let quality = 0.95;
          let canvas = document.createElement("canvas");
          let ctx = canvas.getContext("2d");

          // Resize ảnh nếu quá lớn (max width 1920px)
          let { width, height } = img;
          const maxWidth = 1920;

          if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Giảm quality dần cho đến khi đạt giới hạn kích thước
          let dataUrl = canvas.toDataURL("image/jpeg", quality);
          let currentSize = getBase64Size(dataUrl);

          // Loop giảm quality nếu cần
          while (currentSize > maxSizeBytes && quality > 0.1) {
            quality -= 0.05;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
            currentSize = getBase64Size(dataUrl);
          }

          // Kiểm tra kích thước cuối cùng
          if (currentSize > maxSizeBytes) {
            reject(
              new Error(
                `Không thể nén ảnh xuống ${maxSizeMB}MB. Vui lòng chọn ảnh nhỏ hơn hoặc chất lượng thấp hơn.`
              )
            );
            return;
          }

          resolve({
            dataUrl,
            originalSize,
            compressedSize: currentSize,
            compressed: true,
            quality: (quality * 100).toFixed(0),
          });
        } catch (error) {
          reject(new Error(`Lỗi nén ảnh: ${error.message}`));
        }
      };

      img.onerror = () => reject(new Error("Không thể tải ảnh"));
      img.src = event.target.result;
    };

    reader.onerror = () => reject(new Error("Lỗi đọc file"));
    reader.readAsDataURL(file);
  });
};

/**
 * Tính toán kích thước của base64 string (theo byte)
 * @param {string} dataUrl - Base64 data URL
 * @returns {number} - Kích thước theo byte
 */
const getBase64Size = (dataUrl) => {
  const base64 = dataUrl.split(",")[1];
  return Math.ceil((base64.length * 3) / 4); // Base64 encoding overhead ~33%
};

/**
 * Validate URL ảnh (check extension và accessibility)
 * @param {string} url - Image URL
 * @returns {Promise<boolean>} - true nếu hợp lệ
 */
export const validateImageUrl = async (url) => {
  try {
    // Kiểm tra extension
    const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    const urlLower = url.toLowerCase();
    const hasValidExtension = validExtensions.some((ext) => urlLower.includes(ext));

    if (!hasValidExtension) {
      throw new Error("URL phải là ảnh (jpg, png, gif, webp)");
    }

    // Kiểm tra URL có accessible không (HEAD request)
    const response = await fetch(url, {
      method: "HEAD",
      mode: "no-cors",
    });

    if (response.status !== 200 && response.status !== 0) {
      throw new Error("Không thể truy cập URL ảnh");
    }

    return true;
  } catch (error) {
    throw new Error(`Lỗi validate URL: ${error.message}`);
  }
};

/**
 * Format kích thước file để hiển thị (KB, MB)
 * @param {number} bytes - Kích thước theo byte
 * @returns {string} - Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Chuẩn bị ảnh để gửi lên server
 * @param {Object} image - Image object { type, data, ... }
 * @returns {Object} - { type, data } để gửi
 */
export const prepareImageForUpload = (image) => {
  return {
    type: image.type, // 'url' hoặc 'file'
    data: image.data, // base64 data URL hoặc URL
  };
};

